const KeenSlider = window.KeenSlider

const { setInitialised, useA11y, useArrows } = window.BAO.Carousel.plugins
const { debounce } = window.BAO.Utils

class PushCartCarousel extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.carousel = this.setupCarousel()
  }

  setupListeners () {
    this.listeners.add(
      window,
      'resize',
      debounce(() => {
        this.carousel?.destroy()
        this.carousel = this.setupCarousel()
      }, 500),
    )
  }

  // Note: aria-hidden and tabindex management for non-visible slides is
  // already handled (with correct multi-slide-visible overlap detection) by
  // the shared useA11y() carousel plugin. This method only layers on the
  // supplementary "(Selected)" screen-reader text for visible product links,
  // using the aria-hidden state useA11y has already set, so the two never
  // fight over which slides are focusable/announced.
  updateSlideAccessibility (carousel = this.carousel) {
    if (!this.els.slide?.exists) return

    const slides = this.els.slide?.elements || []

    slides.forEach((slide) => {
      const isVisible = slide.getAttribute('aria-hidden') !== 'true'

      slide.setAttribute('aria-current', String(isVisible))

      // Keep selected state explicit for screen readers on visible product-type links.
      const productTypeLinks = slide.querySelectorAll(
        'a[href]:not([aria-hidden="true"])',
      )

      productTypeLinks.forEach((link) => {
        link.setAttribute('aria-current', String(isVisible))

        let selectedText = link.querySelector(
          '[data-push-cart-carousel-el="selected-text"]',
        )

        if (!selectedText) {
          selectedText = document.createElement('span')
          selectedText.className = 'util-ScreenReaderOnly'
          selectedText.setAttribute(
            'data-push-cart-carousel-el',
            'selected-text',
          )
          link.appendChild(selectedText)
        }

        selectedText.textContent = isVisible ? ' (Selected)' : ''
      })
    })
  }

  // Called when this breakpoint has no KeenSlider carousel (plain scrollable
  // list). Clears any aria-hidden/tabindex="-1"/"(Selected)" state a
  // previously-destroyed carousel instance may have left on slides, since
  // every slide in a scrollable list is reachable and none should be hidden.
  resetSlideAccessibility () {
    if (!this.els.slide?.exists) return

    const slides = this.els.slide?.elements || []

    slides.forEach((slide) => {
      slide.removeAttribute('aria-hidden')
      slide.removeAttribute('aria-current')

      slide
        .querySelectorAll('[data-carousel-original-tabindex]')
        .forEach((element) => {
          const originalTabindex = element.getAttribute(
            'data-carousel-original-tabindex',
          )

          if (originalTabindex) {
            element.setAttribute('tabindex', originalTabindex)
          } else {
            element.removeAttribute('tabindex')
          }

          element.removeAttribute('data-carousel-original-tabindex')
        })

      const selectedText = slide.querySelector(
        '[data-push-cart-carousel-el="selected-text"]',
      )

      if (selectedText) {
        selectedText.textContent = ''
      }

      slide
        .querySelectorAll('a[href]:not([aria-hidden="true"])')
        .forEach((link) => link.removeAttribute('aria-current'))
    })
  }

  setupCarousel () {
    if (!this.els.slide?.exists) return

    if (window.BAO.MediaBreakpoints.isSm) {
      // Not a carousel at this breakpoint (plain scrollable list) - make sure
      // no slide is left stuck aria-hidden/tabindex="-1" from a previous
      // KeenSlider instance (e.g. after resizing from mobile to desktop).
      this.resetSlideAccessibility()

      return
    }

    const carousel = new KeenSlider(
      this.els.slides.element,
      {
        selector: this.els.slide.selector,
        defaultAnimation: {
          duration: 1500,
        },
        loop: false,
        mode: 'snap',
        slides: {
          perView: 2.1,
          spacing: 14,
        },
      },
      [
        setInitialised(),
        useA11y(),
        useArrows({
          next: this.els?.next?.element,
          previous: this.els?.previous?.element,
        }),
      ],
    )

    carousel.on('slideChanged', () => {
      this.updateSlideAccessibility(carousel)
    })

    this.updateSlideAccessibility(carousel)

    return carousel
  }
}

if (!customElements.get('push-cart-carousel')) {
  customElements.define('push-cart-carousel', PushCartCarousel)
}
